import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { WorkAgreementField } from "permissions/resources/work/work-agreement-field";
import { z } from "zod";

export type WorkAgreementAccessJSON = z.infer<typeof WorkAgreementAccessSchema.WorkAgreementAccessJSONZ>;

export type WorkAgreementAccessParams = z.infer<typeof WorkAgreementAccessSchema.WorkAgreementAccessZ>;

export default class WorkAgreementAccessSchema {
  public static readonly WorkAgreementAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementAccess),
    workAgreementId: z.uuid().nullish(),
    // `partialRecord`, not `record`: a grant names only the fields it confers, and Zod 4 made
    // `z.record()` with an enum key EXHAUSTIVE — it demands every member. Under `z.record` a narrowed
    // grant failed to parse, which rejected the whole session-permissions request, left
    // `sessionPermissions` undefined, and hung every page gated on it behind a permanent spinner.
    // Partial is the shape the server actually sends; see `proposal-schema.ts`'s `availableActions`,
    // which hit the same Zod 4 change first.
    fields: z.partialRecord(z.enum(WorkAgreementField), z.enum(AccessLevel)).nullish(),
  });

  public static readonly WorkAgreementAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementAccess),
    workAgreementId: z.instanceof(Guid).optional(),
    fields: z.partialRecord(z.enum(WorkAgreementField), z.enum(AccessLevel)).optional(),
  });

  public static validate(values: unknown): WorkAgreementAccessParams {
    return parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessZ, values, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): WorkAgreementAccessJSON {
    return parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ, values, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is WorkAgreementAccessParams {
    try {
      WorkAgreementAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is WorkAgreementAccessJSON {
    try {
      WorkAgreementAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: WorkAgreementAccessJSON): WorkAgreementAccessParams {
    const parsed = parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ, data, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return WorkAgreementAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: WorkAgreementAccessParams): WorkAgreementAccessJSON {
    const parsed = WorkAgreementAccessSchema.validate(notification);
    return WorkAgreementAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    WorkAgreementAccessSchema.WorkAgreementAccessJSONZ,
    WorkAgreementAccessSchema.WorkAgreementAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.WorkAgreementAccess as const,
        workAgreementId: Guid.fromJSON(data.workAgreementId) ?? undefined,
        fields: (() => {
          if (!data.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(data.fields ?? {})) {
            // Only accept exact enum member names (and not numeric reverse-mapping keys)
            if (Number.isNaN(Number(key)) && key in WorkAgreementField) {
              const field = WorkAgreementField[key as keyof typeof WorkAgreementField] as unknown as WorkAgreementField;
              const level = AccessLevel[value.toString() as keyof typeof AccessLevel];
              if (level) {
                fieldsObj[field] = value;
              } else {
                console.warn(`Unknown access level in WorkAgreementAccess: ${value}`);
              }
            } else {
              console.warn(`Unknown field in WorkAgreementAccess: ${key}`);
            }
          }
          return fieldsObj;
        })(),
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.WorkAgreementAccess as const,
        workAgreementId: resource.workAgreementId?.toJSON(),
        fields: (() => {
          if (!resource.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(resource.fields)) {
            fieldsObj[key as unknown as WorkAgreementField] = value;
          }
          return fieldsObj;
        })(),
      }),
    }
  );
}
